import { pipelineLogger as logger } from '../utils/logger';
import { apiGet, apiPost, apiPut, apiDelete } from './api';

// LocalStorage keys
const PIPELINES_STORAGE_KEY = 'rest_api_pipelines';

// Helper function to get pipelines from localStorage
const getPipelinesFromStorage = () => {
  try {
    const storedData = localStorage.getItem(PIPELINES_STORAGE_KEY);
    return storedData ? JSON.parse(storedData) : [];
  } catch (error) {
    logger.error('Error reading pipelines from localStorage:', error);
    return [];
  }
};

// Helper function to save pipelines to localStorage
const savePipelinesToStorage = (pipelines) => {
  try {
    localStorage.setItem(PIPELINES_STORAGE_KEY, JSON.stringify(pipelines));
    return true;
  } catch (error) {
    logger.error('Error saving pipelines to localStorage:', error);
    return false;
  }
};

// Helper function to save a single pipeline to localStorage
const savePipelineToStorage = (pipeline) => {
  try {
    const pipelines = getPipelinesFromStorage();
    const existingIndex = pipelines.findIndex(p => p.id === pipeline.id);
    
    if (existingIndex >= 0) {
      // Update existing pipeline
      pipelines[existingIndex] = { ...pipeline, updatedAt: new Date().toISOString() };
    } else {
      // Add new pipeline
      const newPipeline = { 
        ...pipeline, 
        id: pipeline.id || `pipeline-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      pipelines.push(newPipeline);
    }
    
    savePipelinesToStorage(pipelines);
    return existingIndex >= 0 ? pipelines[existingIndex] : pipelines[pipelines.length - 1];
  } catch (error) {
    logger.error('Error saving pipeline to localStorage:', error);
    throw error;
  }
};

export const pipelineService = {
  // Get all pipelines
  getPipelines: async () => {
    try {
      const response = await apiGet('pipelines');
      
      // Handle both array response and {data: [...]} response
      let pipelines;
      if (Array.isArray(response)) {
        pipelines = response;
      } else {
        pipelines = response.data || [];
      }
      
      return pipelines;
    } catch (error) {
      
      // Throw the error instead of falling back to localStorage
      throw new Error(`Failed to fetch pipelines from API: ${error.message}`);
    }
  },
  
  // Get a specific pipeline by ID
  getPipeline: async (id) => {
    try {
      const response = await apiGet(`pipelines/${id}`);
      
      // Handle ApiResponse wrapper from backend
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error('Pipeline not found or invalid response');
      }
    } catch (error) {
      throw new Error(`Failed to fetch pipeline: ${error.message}`);
    }
  },
  
  // Create a new pipeline
  createPipeline: async (pipelineData) => {
    try {
      const response = await apiPost('pipelines', pipelineData);
      
      const apiResult = response.data || response;
      return apiResult;
    } catch (error) {
      
      // Throw the error instead of falling back to localStorage
      throw new Error(`Failed to create pipeline on API: ${error.message}`);
    }
  },

  // Save or update a pipeline (smart save)
  savePipeline: async (pipelineData) => {
    try {
      if (pipelineData.id) {
        // Update existing pipeline
        const response = await apiPut(`pipelines/${pipelineData.id}`, pipelineData);
        return response.data || response;
      } else {
        // Create new pipeline
        const response = await apiPost('pipelines', pipelineData);
        return response.data || response;
      }
    } catch (error) {
      throw new Error(`Failed to save pipeline: ${error.message}`);
    }
  },
  
  // Update an existing pipeline
  updatePipeline: async (id, pipelineData) => {
    try {
      const response = await apiPut(`pipelines/${id}`, pipelineData);
      
      const apiResult = response.data || response;
      return apiResult;
    } catch (error) {
      
      // Throw the error instead of falling back to localStorage
      throw new Error(`Failed to update pipeline ${id} on API: ${error.message}`);
    }
  },
  
  // Delete a pipeline
  deletePipeline: async (id) => {
    const response = await apiDelete(`pipelines/${id}`);
    return response.success;
  },
  
  // Start pipeline execution
  startPipeline: async (id) => {
    const response = await apiPost(`pipelines/${id}/start`);
    return response.success;
  },
  
  // Stop pipeline execution
  stopPipeline: async (id) => {
    const response = await apiPost(`pipelines/${id}/stop`);
    return response.success;
  },
  
  // Get pipeline status
  getPipelineStatus: async (id, options = {}) => {
    const response = await apiGet(`pipelines/${id}/status`, options);
    return response.data;
  },
  
  // Get pipeline logs
  getPipelineLogs: async (id, options = {}) => {
    const response = await apiGet(`pipelines/${id}/logs`, options);
    return response.data || [];
  },
  
  // Get pipeline metrics
  getPipelineMetrics: async (id) => {
    const response = await apiGet(`pipelines/${id}/metrics`);
    return response.data;
  },
  
  // Get pipeline analytics with optional date range filters
  getPipelineAnalytics: async (startDate, endDate) => {
    let url = 'pipelines/analytics';
    const params = {};

    if (startDate) {
      params.startDate = startDate;
    }

    if (endDate) {
      params.endDate = endDate;
    }

    const response = await apiGet(url, params);
    return response.data || {};
  },

  // Get pipeline execution history
  getPipelineExecutions: async (pipelineId, days = 7) => {
    try {
      const response = await apiGet(`pipelines/${pipelineId}/executions`, { days });
      return response.data || [];
    } catch (error) {
      logger.error('Error fetching pipeline executions:', error);
      return [];
    }
  },

  // Get logs for a specific execution
  getExecutionLogs: async (pipelineId, executionId) => {
    try {
      const response = await apiGet(`pipelines/${pipelineId}/executions/${executionId}/logs`);
      return response.data || [];
    } catch (error) {
      logger.error('Error fetching execution logs:', error);
      return [];
    }
  },

  // Check if pipeline name is available for a specific type
  checkNameAvailability: async (name, type) => {
    try {
      if (!name || !type) {
        return { available: true, exists: false };
      }

      const response = await apiGet(`pipelines/check-name?name=${encodeURIComponent(name)}&type=${encodeURIComponent(type)}`);

      if (response && response.data) {
        return {
          available: response.data.available,
          exists: response.data.exists
        };
      }

      // Default to available if response is unexpected
      return { available: true, exists: false };
    } catch (error) {
      logger.error('Error checking pipeline name availability:', error);
      // On error, return available=true to not block the user
      return { available: true, exists: false };
    }
  }
};

export default pipelineService;